import React, { PointerEvent, ReactNode, useState } from 'react';


export interface SvgDropTargetDefinition {
  id: string;
  label: string;
  actionId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}


interface SvgPointerDragObjectProps {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  targets: SvgDropTargetDefinition[];
  disabled?: boolean;
  children: ReactNode;
  onDrop: (actionId: string) => void;
  onInvalidDrop?: () => void;
}


interface SvgPoint {
  x: number;
  y: number;
}


const pointerToSvgPoint = (
  event: PointerEvent<SVGGElement>,
): SvgPoint | null => {
  const svg = event.currentTarget.ownerSVGElement;
  if (!svg) return null;
  const bounds = svg.getBoundingClientRect();
  if (!bounds.width || !bounds.height) return null;
  const declaredViewBox = svg.getAttribute('viewBox')
    ?.trim()
    .split(/\s+/)
    .map(Number);
  const viewBox = svg.viewBox?.baseVal;
  const viewBoxX = viewBox?.width ? viewBox.x : (declaredViewBox?.[0] || 0);
  const viewBoxY = viewBox?.height ? viewBox.y : (declaredViewBox?.[1] || 0);
  const viewBoxWidth = viewBox?.width || declaredViewBox?.[2] || bounds.width;
  const viewBoxHeight = viewBox?.height || declaredViewBox?.[3] || bounds.height;
  return {
    x: viewBoxX + ((event.clientX - bounds.left) * viewBoxWidth / bounds.width),
    y: viewBoxY + ((event.clientY - bounds.top) * viewBoxHeight / bounds.height),
  };
};


export const SvgPointerDragObject: React.FC<SvgPointerDragObjectProps> = ({
  id,
  label,
  x,
  y,
  width,
  height,
  targets,
  disabled = false,
  children,
  onDrop,
  onInvalidDrop,
}) => {
  const [pointerId, setPointerId] = useState<number | null>(null);
  const [startPoint, setStartPoint] = useState<SvgPoint | null>(null);
  const [offset, setOffset] = useState<SvgPoint>({ x: 0, y: 0 });
  const [activeTargetId, setActiveTargetId] = useState<string | null>(null);
  const dragging = pointerId !== null;

  const targetAtPoint = (point: SvgPoint | null) => {
    if (!point) return null;
    return targets.find((target) => (
      point.x >= target.x
      && point.x <= target.x + target.width
      && point.y >= target.y
      && point.y <= target.y + target.height
    )) || null;
  };
  const resetDrag = () => {
    setPointerId(null);
    setStartPoint(null);
    setOffset({ x: 0, y: 0 });
    setActiveTargetId(null);
  };
  const handlePointerDown = (event: PointerEvent<SVGGElement>) => {
    if (disabled || event.button > 0) return;
    const point = pointerToSvgPoint(event);
    if (!point) return;
    const activePointerId = event.pointerId || 1;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(activePointerId);
    setPointerId(activePointerId);
    setStartPoint(point);
    setOffset({ x: 0, y: 0 });
  };
  const handlePointerMove = (event: PointerEvent<SVGGElement>) => {
    if (pointerId !== (event.pointerId || 1) || !startPoint) return;
    const point = pointerToSvgPoint(event);
    if (!point) return;
    event.preventDefault();
    setOffset({
      x: point.x - startPoint.x,
      y: point.y - startPoint.y,
    });
    setActiveTargetId(targetAtPoint(point)?.id || null);
  };
  const handlePointerUp = (event: PointerEvent<SVGGElement>) => {
    const activePointerId = event.pointerId || 1;
    if (pointerId !== activePointerId) return;
    const target = targetAtPoint(pointerToSvgPoint(event));
    event.currentTarget.releasePointerCapture?.(activePointerId);
    resetDrag();
    if (target) {
      onDrop(target.actionId);
    } else {
      onInvalidDrop?.();
    }
  };

  return (
    <g data-simulation-drag-object={id}>
      {dragging ? targets.map((target) => (
        <g key={target.id} aria-hidden="true">
          <rect
            x={target.x}
            y={target.y}
            width={target.width}
            height={target.height}
            rx="10"
            className={[
              'simulation-svg-drop-target',
              activeTargetId === target.id ? 'is-active' : '',
            ].filter(Boolean).join(' ')}
          />
          <text
            x={target.x + target.width / 2}
            y={target.y + target.height / 2}
            textAnchor="middle"
            className="simulation-svg-drop-label"
          >
            {target.label}
          </text>
        </g>
      )) : null}
      <g
        transform={`translate(${x + offset.x} ${y + offset.y})`}
        className={[
          'simulation-svg-draggable',
          dragging ? 'is-dragging' : '',
          disabled ? 'is-disabled' : '',
        ].filter(Boolean).join(' ')}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`${label}. Drag to a highlighted destination, or use the destination controls below.`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={resetDrag}
      >
        <rect
          x="-8"
          y="-8"
          width={width + 16}
          height={height + 16}
          rx="8"
          className="simulation-svg-drag-hitbox"
        />
        {children}
      </g>
    </g>
  );
};
